import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, Spinner, Alert, Nav, Tab } from 'react-bootstrap';
import api from '../api/axios';
import QuestionDisplay from './QuestionDisplay';
import { startExam, answerQuestion, setCurrentIndex, completeExam, clearExam } from '../store/examSlice';

const CaseStudyMode = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sessionId, questions, currentIndex, answers } = useSelector((state) => state.exam);
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [selected, setSelected] = useState('');

  // Clear any previous exam state when entering a case study
  useEffect(() => {
    dispatch(clearExam());
  }, [dispatch]);

  // Fetch case study and its questions
  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const res = await api.get(`/case-study.php?id=${id}`);
        setCaseStudy(res.data.caseStudy);
        // Start a new exam session with the case study questions
        dispatch(startExam({
          sessionId: null, // will be set after backend session starts
          mode: 'case_study',
          questions: res.data.questions
        }));
      } catch (err) {
        console.error(err);
        alert('Failed to load case study');
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudy();
  }, [id, dispatch]);

  // Start backend exam session once questions are loaded
  useEffect(() => {
    const startSession = async () => {
      if (questions.length && !sessionId) {
        try {
          const res = await api.post('/exam.php?action=start', {
            mode: 'case_study',
            totalQuestions: questions.length,
            caseStudyId: parseInt(id)
          });
          dispatch(startExam({
            sessionId: res.data.sessionId,
            mode: 'case_study',
            questions // keep the same questions
          }));
        } catch (err) {
          console.error(err);
          alert('Failed to start case study session');
        }
      }
    };
    startSession();
  }, [questions, id, dispatch, sessionId]);

  // Restore selected answer for current question
  useEffect(() => {
    if (questions.length > 0) {
      const qid = questions[currentIndex]?.id;
      if (qid && answers[qid]) {
        setSelected(answers[qid]);
        setSubmitted(true);
      } else {
        setSelected('');
        setSubmitted(false);
      }
    }
  }, [currentIndex, questions, answers]);

  const handleSelect = (opt) => {
    if (!submitted) setSelected(opt);
  };

  const handleSubmit = async () => {
    if (!selected) return alert('Please select an answer');
    const questionId = questions[currentIndex].id;
    try {
      await api.post('/exam.php?action=answer', {
        sessionId,
        questionId,
        selectedOptions: selected,
        currentIndex: currentIndex + 1
      });
      dispatch(answerQuestion({ questionId, selected }));
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Error saving answer');
    }
  };

  const handleNext = () => {
    setSelected('');
    setSubmitted(false);
    if (currentIndex + 1 < questions.length) {
      dispatch(setCurrentIndex(currentIndex + 1));
    } else {
      finishCaseStudy();
    }
  };

  const finishCaseStudy = async () => {
    try {
      const res = await api.post('/exam.php?action=complete', { sessionId });
      dispatch(completeExam({ score: res.data.score, results: res.data.results }));
      // 👇 Pass caseStudyId along with results
      navigate('/case-study/report', { 
        state: { 
          results: res.data.results, 
          totalQuestions: questions.length,
          caseStudyId: id  // <-- added
        } 
      });
    } catch (err) {
      console.error(err);
      alert('Failed to complete case study');
    }
  };

  const handlePause = () => {
    navigate('/dashboard');
  };

  if (loading) return <Container className="mt-5 text-center"><Spinner animation="border" /></Container>;
  if (!questions.length) return <Container className="mt-5"><Alert variant="warning">No questions in this case study.</Alert></Container>;

  const currentQuestion = questions[currentIndex];

  return (
    <Container fluid className="mt-4" style={{ paddingLeft: '20px', paddingRight: '20px' }}>
      <Row>
        {/* Left column: case study tabs */}
        <Col md={5} style={{ borderRight: '1px solid #dee2e6', paddingRight: '20px' }}>
          <h2>{caseStudy?.title}</h2>
          {caseStudy && (
            <Tab.Container defaultActiveKey="history">
              <Nav variant="tabs">
                <Nav.Item><Nav.Link eventKey="history">History & Physical</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="lab">Laboratory</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="vitals">Vital Signs</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="notes">Nurses' Notes</Nav.Link></Nav.Item>
              </Nav>
              <Tab.Content className="mt-3">
                <Tab.Pane eventKey="history">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.history_physical }} />
                </Tab.Pane>
                <Tab.Pane eventKey="lab">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.laboratory }} />
                </Tab.Pane>
                <Tab.Pane eventKey="vitals">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.vital_signs }} />
                </Tab.Pane>
                <Tab.Pane eventKey="notes">
                  <div dangerouslySetInnerHTML={{ __html: caseStudy.nurses_notes }} />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Col>

        {/* Right column: current question */}
        <Col md={7}>
          <p className="text-muted">Question {currentIndex + 1} of {questions.length}</p>
          <Card>
            <Card.Body>
              <p><strong>{currentQuestion.question_text}</strong></p>
              <QuestionDisplay
                question={currentQuestion}
                selectedOptions={selected}
                onSelect={handleSelect}
                showFeedback={false}
                showCorrectness={false}
              />
              {!submitted ? (
                <Button variant="primary" className="mt-3" onClick={handleSubmit}>Submit</Button>
              ) : (
                <Button variant="primary" className="mt-3" onClick={handleNext}>
                  {currentIndex + 1 === questions.length ? 'Finish' : 'Next Question'}
                </Button>
              )}
              <Button variant="outline-secondary" className="mt-3 ms-2" onClick={handlePause}>Pause</Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CaseStudyMode;